import {
    initializeDependentGroupTypeSelects,
    sendFormPostRequest,
    sendPatchRequest,
    sendPostRequest,
} from '../utils';
import { addAlert, genericErrorAlert } from '../alerts';
import { saveWindowState } from '../windows';
import { initializeSelect } from '../manuals/form';
import { updatePing } from '../pings';
import { fetchAndApplyVisibility } from '../form_visibility';
import { pausePrompt } from '../prompts';
import { printLabel } from '../labels';

export function initializeDisassemblyActions() {
    function checkAllConfirmed() {
        const allConfirmed = Array.from(
            document.querySelectorAll('.disassembly-action'),
        ).every((action) => action.dataset.confirmed === 'True');
        if (allConfirmed) {
            document
                .querySelectorAll('.actions-confirmed')
                .forEach((element) => {
                    element.classList.remove('disabled');
                });
        } else {
            document
                .querySelectorAll('.actions-confirmed')
                .forEach((element) => {
                    element.classList.add('disabled');
                });
        }
    }

    checkAllConfirmed();

    document.querySelectorAll('.disassembly-action').forEach((actionBtn) => {
        if (!actionBtn.dataset.url) return;

        actionBtn.addEventListener('click', async function (e) {
            const isConfirmed = actionBtn.dataset.confirmed === 'True';
            const response = await sendPostRequest(actionBtn.dataset.url, {
                is_confirmed: !isConfirmed,
            });
            if (response.ok) {
                actionBtn.dataset.confirmed = isConfirmed ? 'False' : 'True';
                actionBtn.classList.toggle('confirmed');
                checkAllConfirmed();
            } else {
                genericErrorAlert();
            }
        });
    });
}

export function initializeLabelPrinting() {
    const networkPrintBtn = document.getElementById('networkPrintBtn');
    if (networkPrintBtn) {
        networkPrintBtn.addEventListener('click', () => {
            printLabel(networkPrintBtn.dataset.barcode, {
                method: 'network',
                printUrl: networkPrintBtn.dataset.url,
            });
        });
    }

    document.addEventListener('click', async (e) => {
        const btn = e.target.closest('#browserPrintBtn');
        if (!btn || btn.disabled) return;

        btn.disabled = true;
        await printLabel(btn.dataset.barcode, {
            method: 'browser_print',
            zplUrl: btn.dataset.zplUrl,
            sdkUrl: btn.dataset.sdkUrl,
        });
        btn.disabled = false;
    });
}

export function initializeDisassemblyComments() {
    const textarea = document.querySelector('#disassemblyComments');
    textarea.addEventListener('change', async function () {
        const response = await sendPatchRequest(textarea.dataset.url, {
            comments: textarea.value,
        });
        if (!response.ok) {
            genericErrorAlert();
        }
    });
}

export function initializeDisassemblySidePanel() {
    const panel = document.querySelector('#disassemblySidePanel');
    if (!panel) return;

    panel.querySelectorAll('.disassembly-part').forEach((part) => {
        part.addEventListener('click', async function (e) {
            if (part.classList.contains('disabled')) return;

            if (
                [
                    'part-comments-btn',
                    'part-comments',
                    'comments-wrapper',
                ].includes(e.target.className)
            )
                return;
            const response = await sendPostRequest(part.dataset.url);
            const data = await response.json();

            part.classList.remove(part.dataset.status);
            part.classList.add(data.status);
            part.dataset.status = data.status;
            part.querySelector('.status').textContent = data.status_name;

            if (data.status === 'N') {
                part.querySelector('.disassembly-checkbox').checked = false;
            } else {
                part.querySelector('.disassembly-checkbox').checked = true;
            }

            if (data.url) {
                part.dataset.url = data.url;
                part.querySelector('.part-comments').dataset.url = data.url;
            }

            if (['N', 'D'].includes(data.status)) {
                part.querySelector('.part-comments-btn').classList.add(
                    'hidden',
                );
            } else {
                part.querySelector('.part-comments-btn').classList.remove(
                    'hidden',
                );
            }
        });
        const comments = part.querySelector('.part-comments');
        comments.addEventListener('change', async function () {
            const response = await sendPatchRequest(comments.dataset.url, {
                comments: comments.value,
            });
            if (!response.ok) {
                genericErrorAlert();
            }
        });
    });
}

export function initializeEnovaForm(
    form,
    saveCallback = () => {},
    configType = 'KEP',
) {
    form.querySelectorAll('select').forEach((select) => {
        if (select.id !== 'id_group' && select.id !== 'id_type') {
            initializeSelect(select);
        }
    });

    const submitBtn = form.querySelector("button[type='submit']");
    if (!submitBtn) return;

    const idField = form.querySelector('#id_code');
    const groupSelect = form.querySelector('#id_group');
    const typeSelect = form.querySelector('#id_type');
    const warehouseSelect = form.querySelector('#id_warehouse');

    const groupRow = form.querySelector('label[for="id_group"]')?.parentElement;
    const typeRow = form.querySelector('label[for="id_type"]')?.parentElement;
    const warehouseRow = form.querySelector(
        'label[for="id_warehouse"]',
    )?.parentElement;

    const isNewForm = !idField.value;

    if (groupRow) groupRow.classList.add('indicator-red');
    if (warehouseRow && isNewForm) {
        warehouseRow.classList.add('indicator-red');
    }

    const validateForm = () => {
        if (!submitBtn || !groupSelect || !typeSelect) return;

        const isWarehouseRequired = isNewForm && !!warehouseSelect;
        const isTypeRequired =
            typeSelect &&
            typeSelect._choices &&
            typeSelect._choices._currentState.choices.length > 1;
        const isGroupValid = groupSelect.value?.trim() !== '';
        const isWarehouseValid =
            !isWarehouseRequired || warehouseSelect.value?.trim() !== '';
        const isTypeValid = !isTypeRequired || typeSelect.value?.trim() !== '';

        const isFormValid = isGroupValid && isWarehouseValid && isTypeValid;

        submitBtn.disabled = !isFormValid;

        if (groupRow) groupRow.classList.toggle('is-valid', isGroupValid);
        if (warehouseRow && isNewForm) {
            warehouseRow.classList.toggle('is-valid', isWarehouseValid);
        }
        if (typeRow) {
            typeRow.classList.toggle('indicator-red', isTypeRequired);
            typeRow.classList.toggle(
                'is-valid',
                !isTypeRequired || isTypeValid,
            );
        }
    };

    initializeDependentGroupTypeSelects(groupSelect, typeSelect);

    form.querySelectorAll('select').forEach((select) => {
        initializeSelect(select);
    });

    if (groupSelect) groupSelect.addEventListener('change', validateForm);
    if (typeSelect) typeSelect.addEventListener('change', validateForm);
    if (warehouseSelect)
        warehouseSelect.addEventListener('change', validateForm);

    validateForm();

    // Initialize field visibility logic
    if (groupSelect) {
        groupSelect.addEventListener('change', () =>
            fetchAndApplyVisibility(form, configType),
        );
        if (typeSelect) {
            typeSelect.addEventListener('change', () =>
                fetchAndApplyVisibility(form, configType),
            );
        }
        // Initial fetch
        fetchAndApplyVisibility(form, configType);
    }

    function serializeForm(formElement) {
        const formData = new FormData(formElement);
        const intermediateData = {};

        // --- Step 1: Group quantity fields ---
        for (const [key, value] of formData.entries()) {
            if (key.endsWith('_0')) {
                const baseKey = key.slice(0, -2);
                if (!intermediateData[baseKey]) {
                    intermediateData[baseKey] = {};
                }
                intermediateData[baseKey].value = value
                    ? parseFloat(value)
                    : null;
            } else if (key.endsWith('_1')) {
                const baseKey = key.slice(0, -2);
                if (!intermediateData[baseKey]) {
                    intermediateData[baseKey] = {};
                }
                intermediateData[baseKey].unit = value || null;
            } else {
                if (formElement.querySelector(`[name="${key}"]`)?.multiple)
                    continue;
                intermediateData[key] = value;
            }
        }

        // --- Step 2: Build the final nested object from intermediate data ---
        const serializedData = {};
        for (const [path, value] of Object.entries(intermediateData)) {
            const keys = path.split('__');
            let current = serializedData;
            while (keys.length > 1) {
                const key = keys.shift();
                if (!current[key]) {
                    current[key] = {};
                }
                current = current[key];
            }
            current[keys[0]] = value === '' ? null : value;
        }

        // --- Step 3: Handle multi-select fields specifically ---
        const multiSelects = formElement.querySelectorAll('select[multiple]');
        multiSelects.forEach((select) => {
            const selectedOptions = Array.from(select.selectedOptions).map(
                (option) => option.value,
            );
            const path = select.name;
            const keys = path.split('__');
            let current = serializedData;
            while (keys.length > 1) {
                const key = keys.shift();
                if (!current[key]) {
                    current[key] = {};
                }
                current = current[key];
            }
            current[keys[0]] = selectedOptions;
        });

        return serializedData;
    }

    form.addEventListener('submit', async function (event) {
        event.preventDefault();
        const submitBtn = form.querySelector("button[type='submit']");
        submitBtn.disabled = true;
        submitBtn.classList.add('disabled');
        const data = serializeForm(form);
        const idField = form.querySelector('#id_code');
        const deviceField = form.querySelector('#id_device');
        const enovaId = idField?.value;
        const deviceId = deviceField?.value;

        if (deviceId) data.device = deviceId;

        try {
            let response;
            if (enovaId) {
                data.code = enovaId;
                response = await sendPatchRequest(form.dataset.url, data);
            } else {
                response = await sendPostRequest(`${form.dataset.url}`, data);
            }
            const responseData = await response.json();

            addAlert('success', gettext('Zmiany zostały zapisane.'));
            saveCallback(responseData);

            if (idField) idField.value = responseData.enova_id;
            if (deviceField) deviceField.value = responseData.device_enova_id;

            const parentWindow = form.closest('.window');
            if (parentWindow) {
                parentWindow.classList.remove('window-open');
                saveWindowState(parentWindow);
            }
        } catch (error) {
            addAlert(
                'error',
                error.message || gettext('Wystąpił nieoczekiwany błąd.'),
            );
        } finally {
            submitBtn.disabled = false;
            submitBtn.classList.remove('disabled');
        }
    });
}

function isNotificationFormValid(
    requiredFields,
    subjectField,
    recipientListField,
    recipientGroupsField,
) {
    let allFilled = true;

    requiredFields.forEach((field) => {
        const value = field.value ? field.value.trim() : '';

        if (field.type === 'checkbox') {
            if (!field.checked) {
                allFilled = false;
            }
        } else if (field.type === 'file') {
            if (field.required && field.files.length === 0) {
                allFilled = false;
            }
        } else if (field.tagName === 'SELECT') {
            if (field.required && field.selectedOptions.length === 0) {
                allFilled = false;
            } else if (field.required && value === '') {
                allFilled = false;
            }
        } else {
            if (field.required && value === '') {
                allFilled = false;
            }
        }
    });

    if (subjectField) {
        const subjectValue = subjectField.value.trim();
        if (subjectValue.length < 3 || subjectValue.length > 255) {
            allFilled = false;
        }
    }

    if (recipientListField || recipientGroupsField) {
        const hasIndividualRecipients =
            recipientListField && recipientListField.selectedOptions.length > 0;
        const hasGroupRecipients =
            recipientGroupsField &&
            recipientGroupsField.selectedOptions.length > 0;

        if (!hasIndividualRecipients && !hasGroupRecipients) {
            allFilled = false;
        }
    }

    return allFilled;
}

export function initializeGenericNotificationFormHandler({
    form,
    cancelButtonId,
    submitButtonId,
    subjectFieldId = 'id_subject',
    recipientListFieldId = 'id_recipient_id_list',
    recipientGroupsFieldId = null,
    successMessage,
    saveCallback = () => {},
    beforeInitCallback = null,
    fieldsToClearSelectors = [
        '#id_recipient_id_list',
        '#id_subject',
        '#id_message',
        '#id_attachments_input',
    ],
}) {
    if (!form) return;

    const cancelButton = document.getElementById(cancelButtonId);
    const submitButton = document.getElementById(submitButtonId);
    const subjectField = document.getElementById(subjectFieldId);
    const recipientListField = recipientListFieldId
        ? document.getElementById(recipientListFieldId)
        : null;
    const recipientGroupsField = recipientGroupsFieldId
        ? document.getElementById(recipientGroupsFieldId)
        : null;

    if (!submitButton) {
        console.warn(`Submit button with ID "${submitButtonId}" not found.`);
    }

    if (beforeInitCallback) {
        beforeInitCallback(form);
    }

    const requiredFields = Array.from(
        form.querySelectorAll(
            'input[required], select[required], textarea[required]',
        ),
    );

    function toggleSubmitButtonState() {
        if (submitButton) {
            submitButton.disabled = !isNotificationFormValid(
                requiredFields,
                subjectField,
                recipientListField,
                recipientGroupsField,
            );
        }
    }

    requiredFields.forEach((field) => {
        field.addEventListener('input', toggleSubmitButtonState);
        field.addEventListener('change', toggleSubmitButtonState);
    });

    if (subjectField) {
        subjectField.addEventListener('input', toggleSubmitButtonState);
    }

    if (recipientListField) {
        recipientListField.addEventListener('change', toggleSubmitButtonState);
    }

    if (recipientGroupsField) {
        recipientGroupsField.addEventListener(
            'change',
            toggleSubmitButtonState,
        );
    }

    function cleanFormAndCloseWindow() {
        const parentWindow = form.closest('.window');
        if (parentWindow) {
            const closeButton = parentWindow.querySelector('.window-close');

            fieldsToClearSelectors.forEach((selector) => {
                const field = form.querySelector(selector);
                if (field) {
                    if (field._choices) {
                        field._choices.removeActiveItems();
                    } else if (field.type === 'file') {
                        field.value = '';
                    } else if (field.multiple && field.tagName === 'SELECT') {
                        Array.from(field.options).forEach(
                            (option) => (option.selected = false),
                        );
                    } else {
                        field.value = '';
                    }
                } else {
                    console.warn(
                        `Field with selector "${selector}" not found for clearing.`,
                    );
                }
            });

            if (closeButton) {
                closeButton.click();
            }
            toggleSubmitButtonState();
        }
    }

    if (cancelButton) {
        cancelButton.addEventListener('click', (e) => {
            e.preventDefault();
            cleanFormAndCloseWindow();
        });
    }

    toggleSubmitButtonState();

    form.addEventListener('submit', async function (event) {
        event.preventDefault();
        if (!submitButton || submitButton.disabled) return;

        submitButton.disabled = true;
        submitButton.classList.add('disabled');

        const formData = new FormData(form);
        let response;
        try {
            response = await sendFormPostRequest(form.dataset.url, formData);
            const responseData = await response.json();

            if (response.ok) {
                addAlert('success', successMessage);
                saveCallback(responseData);
                cleanFormAndCloseWindow();
            } else {
                if (responseData && responseData.message) {
                    addAlert('error', responseData.message);
                } else if (responseData && typeof responseData === 'object') {
                    let errorMessage = gettext(
                        'Please correct the errors below:',
                    );
                    const errorDetails = [];
                    for (const key in responseData) {
                        if (Array.isArray(responseData[key])) {
                            errorDetails.push(
                                `${key}: ${responseData[key].join(', ')}`,
                            );
                        } else {
                            errorDetails.push(`${key}: ${responseData[key]}`);
                        }
                    }
                    if (errorDetails.length > 0) {
                        errorMessage += '\n' + errorDetails.join('\n');
                    }
                    addAlert('error', errorMessage);
                } else {
                    addAlert(
                        'error',
                        `Error: ${response.status} ${response.statusText}`,
                    );
                }
            }
        } catch (error) {
            console.error('Form submission error:', error);
            addAlert(
                'error',
                gettext('An unexpected error occurred. Please try again.'),
            );
        } finally {
            submitButton.disabled = false;
            submitButton.classList.remove('disabled');
            toggleSubmitButtonState();
        }
    });
}

export function initializeSendEmailForm(form, saveCallback = () => {}) {
    if (!form) return;

    initializeGenericNotificationFormHandler({
        form,
        cancelButtonId: 'cancelSendMail',
        submitButtonId: 'sendMailSubmit',
        successMessage: gettext('Wiadomość mail została wysłana.'),
        saveCallback,
        beforeInitCallback: (currentForm) => {
            const disassemblyIdElement =
                document.getElementById('disassemblyId');
            const inputDisassemblyId =
                document.getElementById('id_disassembly_id');
            if (disassemblyIdElement && inputDisassemblyId) {
                const disassemblyIdValue = disassemblyIdElement.value;
                if (disassemblyIdValue) {
                    inputDisassemblyId.value = disassemblyIdValue;
                }
            }
        },
    });

    const multiSelects = form.querySelectorAll('select[multiple]');

    multiSelects.forEach((select) => {
        if (window.forms && window.forms.initializeSelect) {
            window.forms.initializeSelect(
                select,
                { removeItemButton: true },
                true,
            );
        }
    });
}

export async function checkAndUpdateTips() {
    try {
        const response = await fetch('/api/config/tips/');
        if (!response.ok) {
            console.error('Failed to fetch tips configuration.');
            return;
        }

        const data = await response.json();
        const pingButtons = document.querySelectorAll(
            '.disassembly-footer .ping-button',
        );

        pingButtons.forEach((button) => {
            const updateKey = button.dataset.updateKey;
            if (!updateKey) return;

            const timestampKey = `${updateKey}_updated`;
            const newTimestamp = data[timestampKey];
            const currentTimestamp = button.dataset.updatedAt;

            if (newTimestamp && newTimestamp > currentTimestamp) {
                const windowId = button.dataset.window;
                const windowElement = document.getElementById(windowId);
                if (!windowElement) return;

                const contentElement =
                    windowElement.querySelector('.window-content');
                if (contentElement && data[updateKey]) {
                    contentElement.innerHTML = data[updateKey];
                }

                button.dataset.updatedAt = newTimestamp;
                updatePing(button);
            }
        });
    } catch (error) {
        console.error('Error during checking and updating tips:', error);
    }
}

export function initializeCleaningActions() {
    const groups = document.querySelectorAll('.cleaning-buttons-group');

    groups.forEach((group) => {
        const buttons = group.querySelectorAll('.disassembly-action');

        buttons.forEach((btn) => {
            btn.addEventListener('click', async function (e) {
                if (btn.classList.contains('confirmed')) return;

                const url = group.dataset.url;
                const valueToSend = btn.dataset.value === 'true';

                buttons.forEach((b) => b.classList.remove('confirmed'));
                btn.classList.add('confirmed');

                try {
                    const response = await sendPostRequest(url, {
                        is_confirmed: valueToSend,
                    });

                    if (!response.ok) {
                        throw new Error('API Error');
                    }
                } catch (error) {
                    console.error('Error updating cleaning action:', error);
                    genericErrorAlert();
                }
            });
        });
    });
}

export function initializeTimeTracker() {
    const executionInput = document.getElementById('disassemblyId');
    if (!executionInput || !executionInput.value) return;

    const limitInput = document.getElementById('inactivityLimit');

    const limitSeconds = limitInput ? parseInt(limitInput.value, 10) : 900;
    const inactivityLimitMs = limitSeconds * 1000;

    const executionId = executionInput.value;

    const CONFIG = {
        syncInterval: 10000, // 10s
        inactivityLimit: inactivityLimitMs,
        apiUrl: `/api/execution/${executionId}/track-time`,
        broadcastChannelName: `disassembly_time_tracker_${executionId}`,
    };

    let state = {
        lastTickTime: Date.now(),
        accumulatedTime: 0,
        lastActivityTime: Date.now(),
        isPaused: false,
        isLeader: true,
    };

    // --- Multi-tab synchronization ---
    // Uses BroadcastChannel to ensure only one tab (the "leader") counts time
    // to prevent double-counting when multiple tabs of the same process are open.
    const bc = new BroadcastChannel(CONFIG.broadcastChannelName);
    bc.onmessage = (event) => {
        if (event.data === 'claim_leader') state.isLeader = false;
    };

    function claimLeadership() {
        if (!state.isLeader) {
            state.isLeader = true;
            state.lastTickTime = Date.now();
        }
        bc.postMessage('claim_leader');
    }

    function resetInactivity() {
        if (!state.isLeader) {
            claimLeadership();
        }
        state.lastActivityTime = Date.now();
    }

    window.addEventListener('focus', () => {
        resetInactivity();
        claimLeadership();
    });

    // --- Activity Detection ---
    // Listen for user interactions to reset the inactivity timer.
    // Includes a 1-second throttle to reduce performance impact.
    ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'].forEach(
        (evt) => {
            document.addEventListener(
                evt,
                () => {
                    if (Date.now() - state.lastActivityTime > 1000) {
                        resetInactivity();
                    }
                },
                { passive: true },
            );
        },
    );

    // --- Data Syncing ---
    // Sends accumulated time to the backend.
    // If the request fails (e.g., network error), time is returned to the buffer.
    async function flushTime() {
        if (state.accumulatedTime < 1000) return;
        const timeToSendMs = state.accumulatedTime;
        const secondsToSend = timeToSendMs / 1000;
        state.accumulatedTime = 0;

        try {
            const response = await sendPostRequest(CONFIG.apiUrl, {
                seconds_elapsed: secondsToSend,
            });
            if (!response.ok) throw new Error('API Error');
        } catch (error) {
            state.accumulatedTime += timeToSendMs;
        }
    }

    // --- Main Loop ---
    function tick() {
        const now = Date.now();
        const delta = now - state.lastTickTime;
        state.lastTickTime = now;

        // 1. Check for inactivity
        // If limit exceeded, pause tracking, force flush, and show modal.
        if (now - state.lastActivityTime > CONFIG.inactivityLimit) {
            if (!state.isPaused) {
                state.isPaused = true;

                flushTime();

                pausePrompt({
                    confirmAction: () => {
                        state.isPaused = false;
                        state.lastTickTime = Date.now();
                        state.lastActivityTime = Date.now();
                        claimLeadership();
                    },
                });
            }
        }

        // 2. Accumulate time
        // Only if not paused and this tab is the leader.
        // Delta check | < 120000 (2min) | prevents huge time jumps after PC sleep/wake.
        if (!state.isPaused && state.isLeader) {
            const two_minutes = 120000;
            if (delta < two_minutes) {
                state.accumulatedTime += delta;
            }
        }

        // 3. Trigger upload
        if (state.accumulatedTime >= CONFIG.syncInterval) {
            flushTime();
        }
    }

    setInterval(tick, 1000);

    claimLeadership();

    // --- Safety Net ---
    // Attempts to send any remaining time when the user closes the tab/browser.
    window.addEventListener('beforeunload', () => {
        if (state.accumulatedTime > 0) {
            const data = JSON.stringify({
                seconds_elapsed: state.accumulatedTime / 1000,
            });
            navigator.sendBeacon(CONFIG.apiUrl, data);
        }
    });
}
